import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable, catchError, forkJoin, map, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { FileSpecification } from '../../models/file-specifications/file-specification';
import {
  FileSpecificationCreatePayload,
  FileSpecificationUpdatePayload,
} from '../../models/file-specifications/file-specification-payload';
import {
  FileSpecificationSettings,
  FileSpecificationStillsSettings,
  FileSpecificationVideosSettings,
} from '../../models/file-specifications/file-specification-settings';

type MediaKind = FileSpecificationSettings['media'];

interface StillsBackendEntity {
  id: string;
  organizationId: string;
  name: string;
  value: { settings: Omit<FileSpecificationStillsSettings, 'media'> };
  writeReason: FileSpecification['writeReason'];
  createdAt: string;
}

interface VideosBackendEntity {
  id: string;
  organizationId: string;
  name: string;
  value: { settings: Omit<FileSpecificationVideosSettings, 'media'> };
  writeReason: FileSpecification['writeReason'];
  createdAt: string;
}

interface StillsListResponse {
  stillsFileSpecifications?: StillsBackendEntity[];
}

interface VideosListResponse {
  videosFileSpecifications?: VideosBackendEntity[];
}

interface StillsMutateResponse {
  stillsFileSpecification: StillsBackendEntity;
}

interface VideosMutateResponse {
  videosFileSpecification: VideosBackendEntity;
}

interface StillsAuditResponse {
  revisions?: StillsBackendEntity[];
}

interface VideosAuditResponse {
  revisions?: VideosBackendEntity[];
}

interface StillsDeleteResponse {
  stillsFileSpecification: StillsBackendEntity | null;
  warning: DependentsWarning | null;
}

interface VideosDeleteResponse {
  videosFileSpecification: VideosBackendEntity | null;
  warning: DependentsWarning | null;
}

interface DependentsWarning {
  count: number;
  names: string[];
}

export interface FileSpecificationDeleteResult {
  fileSpecification: FileSpecification | null;
  warning: DependentsWarning | null;
}

@Injectable({
  providedIn: 'root',
})
export class FileSpecificationService {
  constructor(private http: HttpClient) {}

  list(organizationId: string): Observable<FileSpecification[]> {
    if (isDevMode()) {
      console.log('[DEV] [FileSpecificationService] listing', organizationId);
    }
    const stillsUrl = this.collectionUrl(organizationId, 'stills');
    const videosUrl = this.collectionUrl(organizationId, 'videos');
    const stills$ = this.http
      .get<StillsListResponse>(stillsUrl)
      .pipe(map((res) => res.stillsFileSpecifications ?? []));
    const videos$ = this.http
      .get<VideosListResponse>(videosUrl)
      .pipe(map((res) => res.videosFileSpecifications ?? []));
    return forkJoin([stills$, videos$]).pipe(
      map(([stills, videos]) => [
        ...stills.map((entity) => this.reshapeStills(entity)),
        ...videos.map((entity) => this.reshapeVideos(entity)),
      ]),
    );
  }

  getLatest(
    organizationId: string,
    id: string,
    media?: MediaKind,
  ): Observable<FileSpecification> {
    if (isDevMode()) {
      console.log('[DEV] [FileSpecificationService] loading latest', organizationId, id, { media });
    }
    const stillsUrl = `${this.collectionUrl(organizationId, 'stills')}/${id}/latest`;
    const videosUrl = `${this.collectionUrl(organizationId, 'videos')}/${id}/latest`;
    if (media === 'stills') {
      return this.http
        .get<StillsMutateResponse>(stillsUrl)
        .pipe(map((res) => this.reshapeStills(res.stillsFileSpecification)));
    }
    if (media === 'videos') {
      return this.http
        .get<VideosMutateResponse>(videosUrl)
        .pipe(map((res) => this.reshapeVideos(res.videosFileSpecification)));
    }
    return this.http.get<StillsMutateResponse>(stillsUrl).pipe(
      map((res) => this.reshapeStills(res.stillsFileSpecification)),
      catchError((err: HttpErrorResponse) => {
        if (!this.isNotFound(err)) return throwError(() => err);
        return this.http
          .get<VideosMutateResponse>(videosUrl)
          .pipe(map((res) => this.reshapeVideos(res.videosFileSpecification)));
      }),
    );
  }

  create(
    organizationId: string,
    payload: FileSpecificationCreatePayload,
  ): Observable<FileSpecification> {
    if (isDevMode()) {
      console.log('[DEV] [FileSpecificationService] creating', organizationId, payload);
    }
    const media = payload.value.settings.media;
    const url = this.collectionUrl(organizationId, media);
    const body = this.buildRequestBody(payload);
    if (media === 'stills') {
      return this.http
        .post<StillsMutateResponse>(url, body)
        .pipe(map((res) => this.reshapeStills(res.stillsFileSpecification)));
    }
    return this.http
      .post<VideosMutateResponse>(url, body)
      .pipe(map((res) => this.reshapeVideos(res.videosFileSpecification)));
  }

  update(
    organizationId: string,
    id: string,
    payload: FileSpecificationUpdatePayload,
  ): Observable<FileSpecification> {
    if (isDevMode()) {
      console.log('[DEV] [FileSpecificationService] updating', organizationId, id, payload);
    }
    const media = payload.value.settings.media;
    const url = `${this.collectionUrl(organizationId, media)}/${id}`;
    const body = this.buildRequestBody(payload);
    if (media === 'stills') {
      return this.http
        .put<StillsMutateResponse>(url, body)
        .pipe(map((res) => this.reshapeStills(res.stillsFileSpecification)));
    }
    return this.http
      .put<VideosMutateResponse>(url, body)
      .pipe(map((res) => this.reshapeVideos(res.videosFileSpecification)));
  }

  remove(
    organizationId: string,
    id: string,
    acknowledge: boolean = false,
    media?: MediaKind,
  ): Observable<FileSpecificationDeleteResult> {
    if (isDevMode()) {
      console.log('[DEV] [FileSpecificationService] deleting', organizationId, id, {
        acknowledge,
        media,
      });
    }
    const params = { acknowledge: String(acknowledge) };
    const stillsUrl = `${this.collectionUrl(organizationId, 'stills')}/${id}`;
    const videosUrl = `${this.collectionUrl(organizationId, 'videos')}/${id}`;
    if (media === 'stills') {
      return this.http
        .delete<StillsDeleteResponse>(stillsUrl, { params })
        .pipe(map((res) => this.reshapeStillsDelete(res)));
    }
    if (media === 'videos') {
      return this.http
        .delete<VideosDeleteResponse>(videosUrl, { params })
        .pipe(map((res) => this.reshapeVideosDelete(res)));
    }
    return this.http.delete<StillsDeleteResponse>(stillsUrl, { params }).pipe(
      map((res) => this.reshapeStillsDelete(res)),
      catchError((err: HttpErrorResponse) => {
        if (!this.isNotFound(err)) return throwError(() => err);
        return this.http
          .delete<VideosDeleteResponse>(videosUrl, { params })
          .pipe(map((res) => this.reshapeVideosDelete(res)));
      }),
    );
  }

  getAudit(
    organizationId: string,
    id: string,
    media?: MediaKind,
  ): Observable<FileSpecification[]> {
    if (isDevMode()) {
      console.log('[DEV] [FileSpecificationService] loading audit', organizationId, id, { media });
    }
    const stillsUrl = `${this.collectionUrl(organizationId, 'stills')}/${id}/audit`;
    const videosUrl = `${this.collectionUrl(organizationId, 'videos')}/${id}/audit`;
    if (media === 'stills') {
      return this.http
        .get<StillsAuditResponse>(stillsUrl)
        .pipe(map((res) => (res.revisions ?? []).map((entity) => this.reshapeStills(entity))));
    }
    if (media === 'videos') {
      return this.http
        .get<VideosAuditResponse>(videosUrl)
        .pipe(map((res) => (res.revisions ?? []).map((entity) => this.reshapeVideos(entity))));
    }
    return this.http.get<StillsAuditResponse>(stillsUrl).pipe(
      map((res) => (res.revisions ?? []).map((entity) => this.reshapeStills(entity))),
      catchError((err: HttpErrorResponse) => {
        if (!this.isNotFound(err)) return throwError(() => err);
        return this.http
          .get<VideosAuditResponse>(videosUrl)
          .pipe(map((res) => (res.revisions ?? []).map((entity) => this.reshapeVideos(entity))));
      }),
    );
  }

  private collectionUrl(organizationId: string, media: MediaKind): string {
    const slug = media === 'stills' ? 'stills-file-specifications' : 'videos-file-specifications';
    return `${environment.apiBase}/organizations/${organizationId}/${slug}`;
  }

  private reshapeStills(entity: StillsBackendEntity): FileSpecification {
    return {
      id: entity.id,
      organizationId: entity.organizationId,
      name: entity.name,
      value: {
        settings: {
          media: 'stills',
          ...entity.value.settings,
        } as FileSpecificationStillsSettings,
      },
      writeReason: entity.writeReason,
      createdAt: entity.createdAt,
    };
  }

  private reshapeVideos(entity: VideosBackendEntity): FileSpecification {
    return {
      id: entity.id,
      organizationId: entity.organizationId,
      name: entity.name,
      value: {
        settings: {
          media: 'videos',
          ...entity.value.settings,
        } as FileSpecificationVideosSettings,
      },
      writeReason: entity.writeReason,
      createdAt: entity.createdAt,
    };
  }

  private reshapeStillsDelete(res: StillsDeleteResponse): FileSpecificationDeleteResult {
    return {
      fileSpecification: res.stillsFileSpecification
        ? this.reshapeStills(res.stillsFileSpecification)
        : null,
      warning: res.warning,
    };
  }

  private reshapeVideosDelete(res: VideosDeleteResponse): FileSpecificationDeleteResult {
    return {
      fileSpecification: res.videosFileSpecification
        ? this.reshapeVideos(res.videosFileSpecification)
        : null,
      warning: res.warning,
    };
  }

  private buildRequestBody(
    payload: FileSpecificationCreatePayload | FileSpecificationUpdatePayload,
  ): unknown {
    const settingsWithoutMedia = this.stripMedia(payload.value.settings);
    return {
      name: payload.name,
      value: { settings: settingsWithoutMedia },
      ...(payload.writeReason === undefined ? {} : { writeReason: payload.writeReason }),
    };
  }

  private stripMedia(settings: FileSpecificationSettings): Record<string, unknown> {
    const clone: Record<string, unknown> = { ...(settings as unknown as Record<string, unknown>) };
    delete clone['media'];
    return clone;
  }

  private isNotFound(err: HttpErrorResponse): boolean {
    return err.status === 404;
  }
}
